import { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import { naturalSortComparator } from '@editorUtils'
import type { RenderedListRow, Results2ListItem, SortDir, SortMode } from '../types'

/** Ascending compare that also behaves for the -Infinity fallback used for missing values. */
const compareNumbers = (a: number | undefined, b: number | undefined): number => {
  const left = a ?? Number.NEGATIVE_INFINITY
  const right = b ?? Number.NEGATIVE_INFINITY
  if (left === right) return 0
  return left < right ? -1 : 1
}

/**
 * Descending tiebreaker on the trailing metric (anchors: F_d,neu) — 0 for lists whose rows carry
 * none, so it never disturbs their order.
 */
const byTrailingMetricDesc = (a: Results2ListItem, b: Results2ListItem): number =>
  compareNumbers(b.trailingMetric?.sortValue, a.trailingMetric?.sortValue)

/** Rows with nothing in the `qs` column sort last, whichever direction that sort runs in. */
const byQsMissingLast = (a: Results2ListItem, b: Results2ListItem): number =>
  Number(!!a.qsMissing) - Number(!!b.qsMissing)

const buildTreeOrder = (
  items: Results2ListItem[],
  expanded: Record<string, boolean>,
): RenderedListRow[] => {
  const childrenByParent = new Map<string, Results2ListItem[]>()
  for (const item of items) {
    if (item.parent) {
      const list = childrenByParent.get(item.parent) ?? []
      list.push(item)
      childrenByParent.set(item.parent, list)
    }
  }
  const parents = items
    .filter(i => !i.parent)
    .sort((a, b) => (b.eta ?? -Infinity) - (a.eta ?? -Infinity))

  const out: RenderedListRow[] = []
  for (const parent of parents) {
    const isParent = childrenByParent.has(parent.id)
    out.push({ item: parent, depth: 0, isParent })
    if (expanded[parent.id] !== false) {
      for (const child of childrenByParent.get(parent.id) ?? []) {
        out.push({ item: child, depth: 1, isParent: false })
      }
    }
  }
  return out
}

/**
 * Display order of the flat (non-tree) sort modes. Comparators are written ascending and flipped
 * for descending; tiebreakers always stay descending (worst first), like the cross-section mode's
 * η tiebreaker. η and the trailing metric (anchors: F_d,neu) tiebreak each other, so the default
 * η order lists the highest design force first among equally utilized rows.
 */
export const sortListItems = (
  items: Results2ListItem[],
  sortMode: SortMode,
  sortDir: SortDir,
): Results2ListItem[] => {
  const dir = sortDir === 'asc' ? 1 : -1
  const flat = [...items]
  if (sortMode === 'eta')
    flat.sort((a, b) => dir * compareNumbers(a.eta, b.eta) || byTrailingMetricDesc(a, b))
  if (sortMode === 'trailingMetric')
    flat.sort(
      (a, b) =>
        dir * compareNumbers(a.trailingMetric?.sortValue, b.trailingMetric?.sortValue) ||
        compareNumbers(b.eta, a.eta),
    )
  if (sortMode === 'etaC') flat.sort((a, b) => dir * compareNumbers(a.etaC, b.etaC))
  if (sortMode === 'alpha')
    flat.sort((a, b) => dir * naturalSortComparator(a.label ?? a.id, b.label ?? b.id))
  // Both sort on the second column: it holds the cross section on the member
  // lists and the chosen anchor on the Schubanker tab.
  if (sortMode === 'crossSection' || sortMode === 'anchor')
    flat.sort(
      (a, b) =>
        byQsMissingLast(a, b) ||
        dir * naturalSortComparator(a.qs, b.qs) ||
        compareNumbers(b.eta, a.eta),
    )
  return flat
}

type UseRenderedRowsProps = {
  /** All form items — index maps and child counts must cover hidden items too. */
  items: Results2ListItem[]
  /** Items passing the active filters, in `items` order. */
  visibleItems: Results2ListItem[]
  isTree: boolean
  sortMode: SortMode
  /** Direction for the active flat sort mode (ignored in tree mode). */
  sortDir: SortDir
  /** Per-parent expansion state — owned by the parent view state so it survives form remounts. */
  expanded: Record<string, boolean>
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>
}

type UseRenderedRowsResult = {
  /** Rows in display order (tree-expanded or flat-sorted). */
  rendered: RenderedListRow[]
  renderedIndexById: Map<string, number>
  /** Position of each item in the form `items` array (for `items.${index}` field paths). */
  itemGuidToIndex: Record<string, number>
  childCountByParent: Record<string, number>
  toggleExpand: (id: string) => void
}

/** Derives the display order of the virtualized list from the filtered items and sort mode. */
export const useRenderedRows = ({
  items,
  visibleItems,
  isTree,
  sortMode,
  sortDir,
  expanded,
  setExpanded,
}: UseRenderedRowsProps): UseRenderedRowsResult => {
  const toggleExpand = useCallback(
    (id: string) => setExpanded(state => ({ ...state, [id]: state[id] === false })),
    [setExpanded],
  )

  const itemGuidToIndex = useMemo(() => {
    const map: Record<string, number> = {}
    items.forEach((item, index) => {
      map[item.id] = index
    })
    return map
  }, [items])

  const childCountByParent = useMemo(() => {
    const map: Record<string, number> = {}
    for (const item of items) {
      if (!item.parent) continue
      map[item.parent] = (map[item.parent] ?? 0) + 1
    }
    return map
  }, [items])

  const rendered = useMemo<RenderedListRow[]>(() => {
    if (isTree) return buildTreeOrder(visibleItems, expanded)

    return sortListItems(visibleItems, sortMode, sortDir).map(item => ({
      item,
      depth: 0,
      isParent: false,
    }))
  }, [visibleItems, isTree, expanded, sortMode, sortDir])

  const renderedIndexById = useMemo(() => {
    const map = new Map<string, number>()
    rendered.forEach((entry, index) => map.set(entry.item.id, index))
    return map
  }, [rendered])

  return {
    rendered,
    renderedIndexById,
    itemGuidToIndex,
    childCountByParent,
    toggleExpand,
  }
}
